import type { UseQueryReturnType } from '@tanstack/vue-query'
import type { MaybeRef } from 'vue'
import type { AcAssetFamilyCostOfEnergy } from '@/types/analytics/ac-access-family-cost-of-energy.model'
import type { AcAssetFamilySummary } from '@/types/analytics/ac-asset-family-summary.model'
import type { AcPhaseDurationCounts } from '@/types/analytics/ac-phase-duration-counts.model'
import type { AssetFamilyEnergyCertificateData, AssetFamilyEnergyMonthData } from '@/types/analytics/asset-family-energy-data.model'
import type { AssetFamilyGroupEnergy } from '@/types/analytics/asset-family-group-energy.model'
import type { AssetFamilyGroupMembersAnalytics } from '@/types/analytics/asset-family-group-members.model'
import type { AssetFamilyGroupPower } from '@/types/analytics/asset-family-group-power.model'
import type { BatteryBankEnergyReadings } from '@/types/analytics/battery-bank-energy-reading.model'
import type { BatteryDetailData } from '@/types/analytics/battery-detail-data.model'
import type { BatteryMinMaxData } from '@/types/analytics/battery-min-max-data.model'
import type { Co2e } from '@/types/analytics/co2e.model'
import type { CostOfEnergy, CostOfEnergyWithAssets } from '@/types/analytics/cost-of-energy.model'
import type { CurrentAndVoltageData } from '@/types/analytics/current-and-voltage-data.model'
import type { DepthOfDischargeData } from '@/types/analytics/depth-of-discharge-data.model'
import type { EnergyEfficiency } from '@/types/analytics/energy-efficiency.model'
import type { EnergyReadings } from '@/types/analytics/energy-reading.model'
import type { FuelConsumptionReading } from '@/types/analytics/fuel-consumption-reading.model'
import type { FuelLevels } from '@/types/analytics/fuel-levels.model'
import type { FuelThroughputAnalytics } from '@/types/analytics/fuel-throughput.model'
import type { GridSevenDayRollingAverage } from '@/types/analytics/grid-seven-day-rolling-average.model'
import type { NumberWithUnit } from '@/types/analytics/number-with-unit.model'
import type { PhaseDurationCounts } from '@/types/analytics/phase-duration-counts.model'
import type { PhaseDurationTimes } from '@/types/analytics/phase-duration-times.model'
import type { PhaseStatistics } from '@/types/analytics/phase-statistics.model'
import type { PowerProfileData } from '@/types/analytics/power-profile-data.model'
import type { PowerReadings } from '@/types/analytics/power-reading.model'
import type { RunTimeReading } from '@/types/analytics/run-time-reading.model'
import type { SiteAmbientTemperatureModel } from '@/types/analytics/site-ambient-temperature.model'
import type { AcPower, PowerGridAvailabilityTimes, PowerStorage, SiteCard } from '@/types/analytics/site-analytics.model'
import type { SiteCostsAndEmissionsByEnergyUnit } from '@/types/analytics/site-costs-and-emissions-by-energy-unit.model'
import type { SiteCostsAndEmissionsByTypeData } from '@/types/analytics/site-energy-costs-and-emissions-by-type.model'
import type { SiteEnergyData } from '@/types/analytics/site-energy-data.model'
import type { SiteFlow } from '@/types/analytics/site-flow.model'
import type { SiteGroup } from '@/types/analytics/site-groups.model'
import type { SiteLongTermForecast, SiteShortTermForecast } from '@/types/analytics/site-long-term-forecast.model'
import type { SiteMinMaxPowerData } from '@/types/analytics/site-min-max-power-data.model'
import type { SiteNercTariffBandCompliance } from '@/types/analytics/site-nerc-tariff-band-compliance.model'
import type { SitePeakPower } from '@/types/analytics/site-peak-power.model'
import type { SitePowerData } from '@/types/analytics/site-power-data.model'
import type { SiteRunTimeData } from '@/types/analytics/site-run-time-data.model'
import type { SiteRunTimesByType } from '@/types/analytics/site-run-times-by-type.model'
import type { SiteUpTime } from '@/types/analytics/site-up-time.model'
import type { StateOfChargeData } from '@/types/analytics/state-of-charge-data.model'
import type { TotalEnergy } from '@/types/analytics/total-energy.model'
import type { QueryOptions } from '@/types/query-options.model'
import { useQuery } from '@tanstack/vue-query'
import { unref } from 'vue'
import {
  CACHE_KEY_AC_ASSET_FAMILY_COST_OF_ENERGY,
  CACHE_KEY_AC_ASSET_FAMILY_DC_CURRENT_AND_VOLTAGE,
  CACHE_KEY_AC_ASSET_FAMILY_ENERGY_MONTHLY,
  CACHE_KEY_AC_ASSET_FAMILY_SUMMARY,
  CACHE_KEY_ANALYTICS,
  CACHE_KEY_ASSET_FAMILY_GROUP_ENERGY,
  CACHE_KEY_ASSET_FAMILY_GROUP_ENERGY_MONTHLY,
  CACHE_KEY_ASSET_FAMILY_GROUP_MEMBERS,
  CACHE_KEY_ASSET_FAMILY_GROUP_POWER,
  CACHE_KEY_BATTERY_BANK_ENERGY,
  CACHE_KEY_BATTERY_DETAIL,
  CACHE_KEY_CURRENT_AND_VOLTAGE,
  CACHE_KEY_DASHBOARD,
  CACHE_KEY_DEPTH_OF_DISCHARGE,
  CACHE_KEY_ENERGY,
  CACHE_KEY_ENERGY_EFFICIENCY,
  CACHE_KEY_FUEL_CONSUMPTION,
  CACHE_KEY_GRID_SEVEN_DAY_ROLLING_AVERAGE,
  CACHE_KEY_MIN_MAX,
  CACHE_KEY_ORGANISATION_CO2E,
  CACHE_KEY_ORGANISATION_COST_OF_ENERGY,
  CACHE_KEY_ORGANISATION_TOTAL_BIOGAS_POWER,
  CACHE_KEY_ORGANISATION_TOTAL_CONSUMPTION,
  CACHE_KEY_ORGANISATION_TOTAL_ENERGY,
  CACHE_KEY_ORGANISATION_TOTAL_GENERATION,
  CACHE_KEY_ORGANISATION_TOTAL_GENERATOR_POWER,
  CACHE_KEY_ORGANISATION_TOTAL_GRID_POWER,
  CACHE_KEY_ORGANISATION_TOTAL_HYDRO_POWER,
  CACHE_KEY_ORGANISATION_TOTAL_INSTALLED_CAPACITY,
  CACHE_KEY_ORGANISATION_TOTAL_SOLAR_PV_POWER,
  CACHE_KEY_ORGANISATION_TOTAL_STORAGE,
  CACHE_KEY_ORGANISATION_TOTAL_WIND_POWER,
  CACHE_KEY_PHASE_DURATION_AVERAGES,
  CACHE_KEY_PHASE_DURATION_HISTOGRAM,
  CACHE_KEY_PHASE_DURATION_TOTALS,
  CACHE_KEY_PHASE_STATISTICS,
  CACHE_KEY_POWER_GRID_AVAILABILITY_TIMES,
  CACHE_KEY_POWER_PROFILE,
  CACHE_KEY_RUNTIME,
  CACHE_KEY_SITE_AC_ENERGY_CONSUMERS,
  CACHE_KEY_SITE_AC_GENERATORS,
  CACHE_KEY_SITE_AMBIENT_TEMPERATURE,
  CACHE_KEY_SITE_CARD,
  CACHE_KEY_SITE_CO2E_DETAILS,
  CACHE_KEY_SITE_COST_OF_ENERGY,
  CACHE_KEY_SITE_COST_OF_ENERGY_DETAILS,
  CACHE_KEY_SITE_COSTS_AND_EMISSIONS_BY_KILOWATT_HOUR,
  CACHE_KEY_SITE_COSTS_AND_EMISSIONS_BY_TYPE,
  CACHE_KEY_SITE_DC_STORAGE,
  CACHE_KEY_SITE_ENERGY,
  CACHE_KEY_SITE_FLOW,
  CACHE_KEY_SITE_FUEL_LEVEL,
  CACHE_KEY_SITE_FUEL_THROUGHPUT,
  CACHE_KEY_SITE_GROUPS,
  CACHE_KEY_SITE_LONG_TERM_FORECAST,
  CACHE_KEY_SITE_MIN_MAX_POWER,
  CACHE_KEY_SITE_NERC_TARIFF_BAND_COMPLIANCE,
  CACHE_KEY_SITE_PEAK_POWER,
  CACHE_KEY_SITE_POWER,
  CACHE_KEY_SITE_RUN_TIMES,
  CACHE_KEY_SITE_RUN_TIMES_BY_TYPE,
  CACHE_KEY_SITE_SHORT_TERM_FORECAST,
  CACHE_KEY_SITE_TOTAL_ENERGY_DETAILS,
  CACHE_KEY_SITE_UP_TIME,
  CACHE_KEY_STATE_OF_CHARGE,
} from '@/utils/constants/cache'
import { getRequest } from './core/api-client'

async function getCurrentAndVoltage(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<CurrentAndVoltageData> {
  return await getRequest<CurrentAndVoltageData>('/analytics/battery-bank/current-and-voltage', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getCurrentAndVoltageQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<CurrentAndVoltageData>): UseQueryReturnType<CurrentAndVoltageData, Error> {
  return useQuery<CurrentAndVoltageData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_CURRENT_AND_VOLTAGE, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getCurrentAndVoltage(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getPowerProfile(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PowerProfileData> {
  return await getRequest<PowerProfileData>('/analytics/battery-bank/power-profile', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getPowerProfileQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PowerProfileData>): UseQueryReturnType<PowerProfileData, Error> {
  return useQuery<PowerProfileData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_POWER_PROFILE, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getPowerProfile(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getStateOfCharge(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<StateOfChargeData> {
  return await getRequest<StateOfChargeData>('/analytics/battery-bank/state-of-charge', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getStateOfChargeQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<StateOfChargeData>): UseQueryReturnType<StateOfChargeData, Error> {
  return useQuery<StateOfChargeData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_STATE_OF_CHARGE, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getStateOfCharge(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getBatteryMinMax(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<BatteryMinMaxData> {
  return await getRequest<BatteryMinMaxData>('/analytics/battery/min-max', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getBatteryMinMaxQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<BatteryMinMaxData>): UseQueryReturnType<BatteryMinMaxData, Error> {
  return useQuery<BatteryMinMaxData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_MIN_MAX, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getBatteryMinMax(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getBatteryDetail(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<BatteryDetailData> {
  return await getRequest<BatteryDetailData>('/analytics/battery/detail', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getBatteryDetailQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<BatteryDetailData>): UseQueryReturnType<BatteryDetailData, Error> {
  return useQuery<BatteryDetailData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_BATTERY_DETAIL, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getBatteryDetail(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getDepthOfDischarge(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<DepthOfDischargeData> {
  return await getRequest<DepthOfDischargeData>('/analytics/battery-bank/depth-of-discharge', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getDepthOfDischargeQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<DepthOfDischargeData>): UseQueryReturnType<DepthOfDischargeData, Error> {
  return useQuery<DepthOfDischargeData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_DEPTH_OF_DISCHARGE, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getDepthOfDischarge(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getBatteryBankEnergy(batteryBankId: string, startDateTime: string, endDateTime: string, interval: string, signal?: AbortSignal | undefined): Promise<BatteryBankEnergyReadings> {
  return await getRequest<BatteryBankEnergyReadings>('/analytics/battery-bank/energy', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
      interval,
    },
  })
}

export function getBatteryBankEnergyQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, interval: MaybeRef<string>, useQueryOptions?: QueryOptions<BatteryBankEnergyReadings>): UseQueryReturnType<BatteryBankEnergyReadings, Error> {
  return useQuery<BatteryBankEnergyReadings, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_BATTERY_BANK_ENERGY, batteryBankId, interval, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getBatteryBankEnergy(unref(batteryBankId), unref(startDateTime), unref(endDateTime), unref(interval), signal),
    ...useQueryOptions,
  })
}

async function getEnergyEfficiency(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<EnergyEfficiency[]> {
  return await getRequest<EnergyEfficiency[]>('/analytics/battery-bank/energy-efficiency-by-week', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getEnergyEfficiencyQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<EnergyEfficiency[]>): UseQueryReturnType<EnergyEfficiency[], Error> {
  return useQuery<EnergyEfficiency[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ENERGY_EFFICIENCY, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getEnergyEfficiency(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getPhaseDurationAverages(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PhaseDurationTimes[]> {
  return await getRequest<PhaseDurationTimes[]>('/analytics/battery-bank/phase-duration-averages', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getPhaseDurationAveragesQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PhaseDurationTimes[]>): UseQueryReturnType<PhaseDurationTimes[], Error> {
  return useQuery<PhaseDurationTimes[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_PHASE_DURATION_AVERAGES, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getPhaseDurationAverages(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getPhaseDurationTotals(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PhaseDurationTimes[]> {
  return await getRequest<PhaseDurationTimes[]>('/analytics/battery-bank/phase-duration-totals', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getPhaseDurationTotalsQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PhaseDurationTimes[]>): UseQueryReturnType<PhaseDurationTimes[], Error> {
  return useQuery<PhaseDurationTimes[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_PHASE_DURATION_TOTALS, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getPhaseDurationTotals(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getPhaseDurationHistogram(batteryBankId: string, startDateTime: string, endDateTime: string, binDuration: number, maxbin: number, signal?: AbortSignal | undefined): Promise<PhaseDurationCounts[]> {
  return await getRequest<PhaseDurationCounts[]>('/analytics/battery-bank/phase-duration-histogram', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
      binDuration,
      maxbin,
    },
  })
}

export function getPhaseDurationHistogramQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, binDuration: MaybeRef<number>, maxbin: MaybeRef<number>, useQueryOptions?: QueryOptions<PhaseDurationCounts[]>): UseQueryReturnType<PhaseDurationCounts[], Error> {
  return useQuery<PhaseDurationCounts[], Error>({
    queryKey: [
      CACHE_KEY_ANALYTICS,
      CACHE_KEY_PHASE_DURATION_HISTOGRAM,
      batteryBankId,
      binDuration,
      maxbin,
      [startDateTime, endDateTime],
    ],
    queryFn: async ({ signal }) =>
      await getPhaseDurationHistogram(
        unref(batteryBankId),
        unref(startDateTime),
        unref(endDateTime),
        unref(binDuration),
        unref(maxbin),
        signal,
      ),
    ...useQueryOptions,
  })
}

async function getAcPhaseDurationHistogram(assetFamilyId: string, startDateTime: string, endDateTime: string, binDuration: number, maxbin: number, signal?: AbortSignal | undefined): Promise<AcPhaseDurationCounts[]> {
  return await getRequest<AcPhaseDurationCounts[]>('/analytics/ac-asset-family/phase-duration-histogram', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
      binDuration,
      maxbin,
    },
  })
}

export function getAcPhaseDurationHistogramQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, binDuration: MaybeRef<number>, maxbin: MaybeRef<number>, useQueryOptions?: QueryOptions<AcPhaseDurationCounts[]>): UseQueryReturnType<AcPhaseDurationCounts[], Error> {
  return useQuery<AcPhaseDurationCounts[], Error>({
    queryKey: [
      CACHE_KEY_ANALYTICS,
      CACHE_KEY_PHASE_DURATION_HISTOGRAM,
      assetFamilyId,
      binDuration,
      maxbin,
      [startDateTime, endDateTime],
    ],
    queryFn: async ({ signal }) =>
      await getAcPhaseDurationHistogram(
        unref(assetFamilyId),
        unref(startDateTime),
        unref(endDateTime),
        unref(binDuration),
        unref(maxbin),
        signal,
      ),
    ...useQueryOptions,
  })
}

async function getAcPower(assetFamilyId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PowerReadings> {
  return await getRequest<PowerReadings>('/analytics/ac-asset-family/power', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getAcPowerQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PowerReadings>): UseQueryReturnType<PowerReadings, Error> {
  return useQuery<PowerReadings, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_POWER_PROFILE, assetFamilyId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getAcPower(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getAcEnergy(assetFamilyId: string, startDateTime: string, endDateTime: string, interval: string, signal?: AbortSignal | undefined): Promise<EnergyReadings> {
  return await getRequest<EnergyReadings>('/analytics/ac-asset-family/energy', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
      interval,
    },
  })
}

export function getAcEnergyQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, interval: MaybeRef<string>, useQueryOptions?: QueryOptions<EnergyReadings>): UseQueryReturnType<EnergyReadings, Error> {
  return useQuery<EnergyReadings, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ENERGY, assetFamilyId, interval, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAcEnergy(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), unref(interval), signal),
    ...useQueryOptions,
  })
}

async function getAcRuntime(assetFamilyId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<RunTimeReading[]> {
  return await getRequest<RunTimeReading[]>('/analytics/ac-asset-family/run-times', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getAcRuntimeQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<RunTimeReading[]>): UseQueryReturnType<RunTimeReading[], Error> {
  return useQuery<RunTimeReading[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_RUNTIME, assetFamilyId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getAcRuntime(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getBatteryBankPhaseStatistics(batteryBankId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PhaseStatistics[]> {
  return await getRequest<PhaseStatistics[]>('/analytics/battery-bank/phase-statistics', {
    signal,
    params: {
      batteryBankId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getBatteryBankPhaseStatisticsQuery(batteryBankId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PhaseStatistics[]>): UseQueryReturnType<PhaseStatistics[], Error> {
  return useQuery<PhaseStatistics[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_PHASE_STATISTICS, batteryBankId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getBatteryBankPhaseStatistics(unref(batteryBankId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getAcPhaseStatistics(assetFamilyId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PhaseStatistics[]> {
  return await getRequest<PhaseStatistics[]>('/analytics/ac-asset-family/phase-statistics', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getAcPhaseStatisticsQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PhaseStatistics[]>): UseQueryReturnType<PhaseStatistics[], Error> {
  return useQuery<PhaseStatistics[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_PHASE_STATISTICS, assetFamilyId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAcPhaseStatistics(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getGeneratorFuelConsumption(assetFamilyId: string, startDateTime: string, endDateTime: string, interval: string, signal?: AbortSignal | undefined): Promise<FuelConsumptionReading[]> {
  return await getRequest<FuelConsumptionReading[]>('/analytics/generator/fuel-consumption', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
      interval,
    },
  })
}

export function getGeneratorFuelConsumptionQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, interval: MaybeRef<string>, useQueryOptions?: QueryOptions<FuelConsumptionReading[]>): UseQueryReturnType<FuelConsumptionReading[], Error> {
  return useQuery<FuelConsumptionReading[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_FUEL_CONSUMPTION, assetFamilyId, interval, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getGeneratorFuelConsumption(
        unref(assetFamilyId),
        unref(startDateTime),
        unref(endDateTime),
        unref(interval),
        signal,
      ),
    ...useQueryOptions,
  })
}

async function getAcAssetFamilyDcCurrentAndVoltage(assetFamilyId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<CurrentAndVoltageData> {
  return await getRequest<CurrentAndVoltageData>('/analytics/ac-asset-family/ac-dc-current-and-voltage', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getAcAssetFamilyDcCurrentAndVoltageQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<CurrentAndVoltageData>): UseQueryReturnType<CurrentAndVoltageData, Error> {
  return useQuery<CurrentAndVoltageData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_AC_ASSET_FAMILY_DC_CURRENT_AND_VOLTAGE, assetFamilyId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAcAssetFamilyDcCurrentAndVoltage(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getAcAssetFamilyCostOfEnergy(assetFamilyId: string, startDateTime: string, endDateTime: string, interval: string, signal?: AbortSignal | undefined): Promise<AcAssetFamilyCostOfEnergy[]> {
  return await getRequest<AcAssetFamilyCostOfEnergy[]>('/analytics/ac-asset-family/cost-of-energy', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
      interval,
    },
  })
}

export function getAcAssetFamilyCostOfEnergyQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, interval: MaybeRef<string>, useQueryOptions?: QueryOptions<AcAssetFamilyCostOfEnergy[]>): UseQueryReturnType<AcAssetFamilyCostOfEnergy[], Error> {
  return useQuery<AcAssetFamilyCostOfEnergy[], Error>({
    queryKey: [
      CACHE_KEY_ANALYTICS,
      CACHE_KEY_AC_ASSET_FAMILY_COST_OF_ENERGY,
      assetFamilyId,
      interval,
      [startDateTime, endDateTime],
    ],
    queryFn: async ({ signal }) =>
      await getAcAssetFamilyCostOfEnergy(
        unref(assetFamilyId),
        unref(startDateTime),
        unref(endDateTime),
        unref(interval),
        signal,
      ),
    ...useQueryOptions,
  })
}

async function getAcAssetFamilyEnergy(assetFamilyId: string, startDateTime: string, endDateTime: string, interval: string, signal?: AbortSignal | undefined): Promise<AcAssetFamilySummary> {
  return await getRequest<AcAssetFamilySummary>('/analytics/ac-asset-family-summary', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
      interval,
    },
  })
}

export function getAcAssetFamilyEnergyQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, interval: MaybeRef<string>, useQueryOptions?: QueryOptions<AcAssetFamilySummary>): UseQueryReturnType<AcAssetFamilySummary, Error> {
  return useQuery<AcAssetFamilySummary, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_AC_ASSET_FAMILY_SUMMARY, assetFamilyId, interval, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAcAssetFamilyEnergy(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), unref(interval), signal),
    ...useQueryOptions,
  })
}

async function getAcAssetFamilyEnergyByMonth(assetFamilyId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal): Promise<AssetFamilyEnergyMonthData> {
  return await getRequest<AssetFamilyEnergyMonthData>('/analytics/ac-asset-family/energy-by-month', {
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
    },
    signal,
  })
}

export function getAcAssetFamilyEnergyMonthlyQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<AssetFamilyEnergyMonthData>): UseQueryReturnType<AssetFamilyEnergyMonthData, Error> {
  return useQuery<AssetFamilyEnergyMonthData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_AC_ASSET_FAMILY_ENERGY_MONTHLY, assetFamilyId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAcAssetFamilyEnergyByMonth(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getAcAssetFamilyPower(assetFamilyId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PowerReadings> {
  return await getRequest<PowerReadings>('/analytics/ac-asset-family/power', {
    signal,
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getAcAssetFamilyPowerQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PowerReadings>): UseQueryReturnType<PowerReadings, Error> {
  return useQuery<PowerReadings, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_AC_ASSET_FAMILY_SUMMARY, assetFamilyId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAcAssetFamilyPower(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteAcEnergyConsumers(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<AcPower> {
  return await getRequest<AcPower>('/analytics/site/ac-consumers', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSiteAcEnergyConsumersQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<AcPower>): UseQueryReturnType<AcPower, Error> {
  return useQuery<AcPower, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_AC_ENERGY_CONSUMERS, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getSiteAcEnergyConsumers(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteAcGenerators(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<AcPower> {
  return await getRequest<AcPower>('/analytics/site/ac-generators', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSiteAcGeneratorsQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<AcPower>): UseQueryReturnType<AcPower, Error> {
  return useQuery<AcPower, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_AC_GENERATORS, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteAcGenerators(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteDcStorage(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PowerStorage> {
  return await getRequest<PowerStorage>('/analytics/site/dc-storage', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSiteDcStorageQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PowerStorage>): UseQueryReturnType<PowerStorage, Error> {
  return useQuery<PowerStorage, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_DC_STORAGE, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteDcStorage(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

// GET /analytics/site/fuel-level
async function getSiteFuelLevels(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<FuelLevels[]> {
  return await getRequest<FuelLevels[]>('/analytics/site/fuel-level', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSiteFuelLevelsQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<FuelLevels[]>): UseQueryReturnType<FuelLevels[], Error> {
  return useQuery<FuelLevels[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_FUEL_LEVEL, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteFuelLevels(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

// GET /analytics/site/fuel-throughput
async function getSiteFuelThroughput(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<FuelThroughputAnalytics[]> {
  return await getRequest<FuelThroughputAnalytics[]>('/analytics/site/fuel-throughput', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSiteFuelThroughputQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<FuelThroughputAnalytics[]>): UseQueryReturnType<FuelThroughputAnalytics[], Error> {
  return useQuery<FuelThroughputAnalytics[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_FUEL_THROUGHPUT, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteFuelThroughput(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getAssetFamilyGroupEnergy(assetFamilyGroupId: string, startDateTime: string, endDateTime: string, interval: string, signal?: AbortSignal | undefined): Promise<AssetFamilyGroupEnergy> {
  return await getRequest<AssetFamilyGroupEnergy>('/analytics/asset-family-group/energy', {
    signal,
    params: {
      assetFamilyGroupId,
      startDateTime,
      endDateTime,
      interval,
    },
  })
}

export function getAssetFamilyGroupEnergyQuery(assetFamilyGroupId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, interval: MaybeRef<string>, useQueryOptions?: QueryOptions<AssetFamilyGroupEnergy>): UseQueryReturnType<AssetFamilyGroupEnergy, Error> {
  return useQuery<AssetFamilyGroupEnergy, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ASSET_FAMILY_GROUP_ENERGY, assetFamilyGroupId, interval, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAssetFamilyGroupEnergy(unref(assetFamilyGroupId), unref(startDateTime), unref(endDateTime), unref(interval), signal),
    ...useQueryOptions,
  })
}

async function getAssetFamilyGroupPower(assetFamilyGroupId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<AssetFamilyGroupPower> {
  return await getRequest<AssetFamilyGroupPower>('/analytics/asset-family-group/power', {
    signal,
    params: {
      assetFamilyGroupId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getAssetFamilyGroupPowerQuery(assetFamilyGroupId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<AssetFamilyGroupPower>): UseQueryReturnType<AssetFamilyGroupPower, Error> {
  return useQuery<AssetFamilyGroupPower, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ASSET_FAMILY_GROUP_POWER, assetFamilyGroupId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAssetFamilyGroupPower(unref(assetFamilyGroupId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

export function getAssetFamilyGroupEnergyMonthlyQuery(assetFamilyGroupId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<AssetFamilyGroupEnergy>): UseQueryReturnType<AssetFamilyGroupEnergy, Error> {
  return useQuery<AssetFamilyGroupEnergy, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ASSET_FAMILY_GROUP_ENERGY_MONTHLY, assetFamilyGroupId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getAssetFamilyGroupEnergy(unref(assetFamilyGroupId), unref(startDateTime), unref(endDateTime), '1M', signal),
    ...useQueryOptions,
  })
}

async function getAssetFamilyGroupMembers(assetFamilyGroupId: string, signal?: AbortSignal | undefined): Promise<AssetFamilyGroupMembersAnalytics> {
  return await getRequest<AssetFamilyGroupMembersAnalytics>('/analytics/asset-family-group/members', {
    signal,
    params: {
      assetFamilyGroupId,
    },
  })
}

export function getAssetFamilyGroupMembersQuery(assetFamilyGroupId: MaybeRef<string>, useQueryOptions?: QueryOptions<AssetFamilyGroupMembersAnalytics>): UseQueryReturnType<AssetFamilyGroupMembersAnalytics, Error> {
  return useQuery<AssetFamilyGroupMembersAnalytics, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ASSET_FAMILY_GROUP_MEMBERS, assetFamilyGroupId],
    queryFn: async ({ signal }) => await getAssetFamilyGroupMembers(unref(assetFamilyGroupId), signal),
    ...useQueryOptions,
  })
}

// Need to export this so that it can be used in sites.api.ts
export async function getSiteCard(siteId: string, signal?: AbortSignal | undefined): Promise<SiteCard> {
  return await getRequest<SiteCard>('/analytics/site/card', {
    signal,
    params: {
      siteId,
    },
  })
}

export function getSiteCardQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteCard>): UseQueryReturnType<SiteCard, Error> {
  return useQuery<SiteCard, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_CARD, siteId],
    queryFn: async ({ signal }) => await getSiteCard(unref(siteId), signal),
    ...useQueryOptions,
  })
}
async function getSiteAmbientTemperatureHistorical(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<SiteAmbientTemperatureModel[]> {
  return await getRequest<SiteAmbientTemperatureModel[]>('/analytics/site/ambient-temperature-historical', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSiteAmbientTemperatureHistoricalQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteAmbientTemperatureModel[]>): UseQueryReturnType<SiteAmbientTemperatureModel[], Error> {
  return useQuery<SiteAmbientTemperatureModel[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_AMBIENT_TEMPERATURE, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getSiteAmbientTemperatureHistorical(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteCostOfEnergy(siteId: string, signal?: AbortSignal | undefined): Promise<CostOfEnergy> {
  return await getRequest<CostOfEnergy>('/analytics/site/cost-of-energy', {
    signal,
    params: {
      siteId,
    },
  })
}

export function getSiteCostOfEnergyQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<CostOfEnergy>): UseQueryReturnType<CostOfEnergy, Error> {
  return useQuery<CostOfEnergy, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_COST_OF_ENERGY, siteId],
    queryFn: async ({ signal }) => await getSiteCostOfEnergy(unref(siteId), signal),
    ...useQueryOptions,
  })
}

async function getSiteMinMaxPower(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<SiteMinMaxPowerData> {
  return await getRequest<SiteMinMaxPowerData>('/analytics/site/min-max-power', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSiteMinMaxPowerQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteMinMaxPowerData>): UseQueryReturnType<SiteMinMaxPowerData, Error> {
  return useQuery<SiteMinMaxPowerData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_MIN_MAX_POWER, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteMinMaxPower(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSitePower(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<SitePowerData> {
  return await getRequest<SitePowerData>('/analytics/site/power', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSitePowerQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SitePowerData>): UseQueryReturnType<SitePowerData, Error> {
  return useQuery<SitePowerData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_POWER, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSitePower(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteEnergy(siteId: string, startDateTime: string, endDateTime: string, interval: string, signal?: AbortSignal | undefined): Promise<SiteEnergyData> {
  return await getRequest<SiteEnergyData>('/analytics/site/energy', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
      interval,
    },
  })
}

export function getSiteEnergyQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, interval: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteEnergyData>): UseQueryReturnType<SiteEnergyData, Error> {
  return useQuery<SiteEnergyData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_ENERGY, siteId, interval, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) =>
      await getSiteEnergy(unref(siteId), unref(startDateTime), unref(endDateTime), unref(interval), signal),
    ...useQueryOptions,
  })
}

async function getSiteRunTimes(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<SiteRunTimeData> {
  return await getRequest<SiteRunTimeData>('/analytics/site/run-times', {
    signal,
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
  })
}

export function getSiteRunTimesQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteRunTimeData>): UseQueryReturnType<SiteRunTimeData, Error> {
  return useQuery<SiteRunTimeData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_RUN_TIMES, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteRunTimes(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalConsumption(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-consumption', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalConsumptionQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_CONSUMPTION, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalConsumption(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalGeneration(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-generation', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalGenerationQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_GENERATION, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalGeneration(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalStorage(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-storage', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalStorageQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_STORAGE, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalStorage(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalGeneratorPower(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-generator-power', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalGeneratorPowerQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_GENERATOR_POWER, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalGeneratorPower(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalGridPower(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-grid-power', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalGridPowerQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_GRID_POWER, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalGridPower(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalSolarPvPower(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-solar-pv-power', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalSolarPvPowerQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_SOLAR_PV_POWER, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalSolarPvPower(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalBiogasPower(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-biogas-power', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalBiogasPowerQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_BIOGAS_POWER, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalBiogasPower(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalHydroPower(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-hydro-power', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalHydroPowerQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_HYDRO_POWER, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalHydroPower(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalWindPower(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-wind-power', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalWindPowerQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_WIND_POWER, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalWindPower(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalInstalledCapacity(orgId: string, signal?: AbortSignal | undefined): Promise<NumberWithUnit> {
  return await getRequest<NumberWithUnit>('/analytics/organisation/total-installed-capacity', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalInstalledCapacityQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<NumberWithUnit>): UseQueryReturnType<NumberWithUnit, Error> {
  return useQuery<NumberWithUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_ORGANISATION_TOTAL_INSTALLED_CAPACITY, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalInstalledCapacity(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationCostOfEnergy(orgId: string, signal?: AbortSignal | undefined): Promise<CostOfEnergyWithAssets> {
  return await getRequest<CostOfEnergyWithAssets>('/analytics/organisation/cost-of-energy', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationCostOfEnergyQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<CostOfEnergyWithAssets>): UseQueryReturnType<CostOfEnergyWithAssets, Error> {
  return useQuery<CostOfEnergyWithAssets, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_DASHBOARD, CACHE_KEY_ORGANISATION_COST_OF_ENERGY, orgId],
    queryFn: async ({ signal }) => await getOrganisationCostOfEnergy(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationCo2e(orgId: string, signal?: AbortSignal | undefined): Promise<Co2e> {
  return await getRequest<Co2e>('/analytics/organisation/co2e', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationCo2eQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<Co2e>): UseQueryReturnType<Co2e, Error> {
  return useQuery<Co2e, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_DASHBOARD, CACHE_KEY_ORGANISATION_CO2E, orgId],
    queryFn: async ({ signal }) => await getOrganisationCo2e(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getOrganisationTotalEnergy(orgId: string, signal?: AbortSignal | undefined): Promise<TotalEnergy> {
  return await getRequest<TotalEnergy>('/analytics/organisation/total-energy', {
    params: {
      orgId,
    },
    signal,
  })
}

export function getOrganisationTotalEnergyQuery(orgId: MaybeRef<string>, useQueryOptions?: QueryOptions<TotalEnergy>): UseQueryReturnType<TotalEnergy, Error> {
  return useQuery<TotalEnergy, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_DASHBOARD, CACHE_KEY_ORGANISATION_TOTAL_ENERGY, orgId],
    queryFn: async ({ signal }) => await getOrganisationTotalEnergy(unref(orgId), signal),
    ...useQueryOptions,
  })
}

async function getPowerGridAvailabilityTimes(assetFamilyId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<PowerGridAvailabilityTimes[]> {
  return await getRequest<PowerGridAvailabilityTimes[]>('/analytics/power-grid/availability-times', {
    params: {
      assetFamilyId,
      startDateTime,
      endDateTime,
    },
    signal,
  })
}

export function getPowerGridAvailabilityTimesQuery(assetFamilyId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<PowerGridAvailabilityTimes[]>): UseQueryReturnType<PowerGridAvailabilityTimes[], Error> {
  return useQuery<PowerGridAvailabilityTimes[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_POWER_GRID_AVAILABILITY_TIMES, assetFamilyId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getPowerGridAvailabilityTimes(unref(assetFamilyId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getGridSevenDayRollingAverage(gridId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<GridSevenDayRollingAverage> {
  return await getRequest<GridSevenDayRollingAverage>('/analytics/grid/seven-day-rolling-average', {
    params: {
      endDateTime,
      gridId,
      startDateTime,
    },
    signal,
  })
}

export function getGridSevenDayRollingAverageQuery(gridId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<GridSevenDayRollingAverage>): UseQueryReturnType<GridSevenDayRollingAverage, Error> {
  return useQuery<GridSevenDayRollingAverage, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_GRID_SEVEN_DAY_ROLLING_AVERAGE, gridId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getGridSevenDayRollingAverage(unref(gridId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteCostOfEnergyDetails(siteId: string, signal?: AbortSignal): Promise<CostOfEnergyWithAssets> {
  return await getRequest<CostOfEnergyWithAssets>('analytics/site/cost-of-energy-details', {
    params: {
      siteId,
    },
    signal,
  })
}

export function getSiteCostOfEnergyDetailsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<CostOfEnergyWithAssets>): UseQueryReturnType<CostOfEnergyWithAssets, Error> {
  return useQuery<CostOfEnergyWithAssets, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_COST_OF_ENERGY_DETAILS, siteId],
    queryFn: async ({ signal }) => await getSiteCostOfEnergyDetails(unref(siteId), signal),
    ...useQueryOptions,
  })
}

async function getSiteCo2eDetails(siteId: string, signal?: AbortSignal): Promise<Co2e> {
  return await getRequest<Co2e>('analytics/site/co2e', {
    params: {
      siteId,
    },
    signal,
  })
}

export function getSiteCo2eDetailsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<Co2e>): UseQueryReturnType<Co2e, Error> {
  return useQuery<Co2e, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_CO2E_DETAILS, siteId],
    queryFn: async ({ signal }) => await getSiteCo2eDetails(unref(siteId), signal),
    ...useQueryOptions,
  })
}

async function getSiteTotalEnergyDetails(siteId: string, signal?: AbortSignal): Promise<TotalEnergy> {
  return await getRequest<TotalEnergy>('analytics/site/total-energy', {
    params: {
      siteId,
    },
    signal,
  })
}

export function getSiteTotalEnergyDetailsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<TotalEnergy>): UseQueryReturnType<TotalEnergy, Error> {
  return useQuery<TotalEnergy, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_TOTAL_ENERGY_DETAILS, siteId],
    queryFn: async ({ signal }) => await getSiteTotalEnergyDetails(unref(siteId), signal),
    ...useQueryOptions,
  })
}

async function getSiteUpTime(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<SiteUpTime[]> {
  return await getRequest<SiteUpTime[]>('/analytics/site/up-time', {
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
    signal,
  })
}

export function getSiteUpTimeQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteUpTime[]>): UseQueryReturnType<SiteUpTime[], Error> {
  return useQuery<SiteUpTime[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_UP_TIME, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteUpTime(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteEnergyCostsAndEmissionsByType(siteId: string, startDateTime: string, endDateTime: string, interval: string | undefined, includeEfficiencySaving: boolean, signal?: AbortSignal | undefined): Promise<SiteCostsAndEmissionsByTypeData> {
  return await getRequest<SiteCostsAndEmissionsByTypeData>('/analytics/site/energy-costs-and-emissions-by-type', {
    params: {
      siteId,
      startDateTime,
      endDateTime,
      interval,
      includeEfficiencySaving,
    },
    signal,
  })
}

export function getSiteEnergyCostsAndEmissionsByTypeQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, interval: MaybeRef<string | undefined>, includeEfficiencySaving: MaybeRef<boolean>, useQueryOptions?: QueryOptions<SiteCostsAndEmissionsByTypeData>): UseQueryReturnType<SiteCostsAndEmissionsByTypeData, Error> {
  return useQuery<SiteCostsAndEmissionsByTypeData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_COSTS_AND_EMISSIONS_BY_TYPE, siteId, [startDateTime, endDateTime, interval, includeEfficiencySaving]],
    queryFn: async ({ signal }) => await getSiteEnergyCostsAndEmissionsByType(unref(siteId), unref(startDateTime), unref(endDateTime), unref(interval), unref(includeEfficiencySaving), signal),
    ...useQueryOptions,
  })
}

async function getSiteCostsAndEmissionsPerEnergyUnit(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<SiteCostsAndEmissionsByEnergyUnit> {
  return await getRequest<SiteCostsAndEmissionsByEnergyUnit>('/analytics/site/costs-and-emissions-per-energy-unit', {
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
    signal,
  })
}

export function getSiteCostsAndEmissionsPerEnergyUnitQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteCostsAndEmissionsByEnergyUnit>): UseQueryReturnType<SiteCostsAndEmissionsByEnergyUnit, Error> {
  return useQuery<SiteCostsAndEmissionsByEnergyUnit, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_COSTS_AND_EMISSIONS_BY_KILOWATT_HOUR, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteCostsAndEmissionsPerEnergyUnit(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteGroups(siteId: string, signal?: AbortSignal | undefined): Promise<SiteGroup> {
  return await getRequest<SiteGroup>('/analytics/site/groups', {
    params: {
      siteId,
    },
    signal,
  })
}

export function getSiteGroupsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteGroup>): UseQueryReturnType<SiteGroup, Error> {
  return useQuery<SiteGroup, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_GROUPS, siteId],
    queryFn: async ({ signal }) => await getSiteGroups(unref(siteId), signal),
    ...useQueryOptions,
  })
}

async function getSitePeakPower(siteId: string, dateTime: string, signal?: AbortSignal | undefined): Promise<SitePeakPower> {
  return await getRequest<SitePeakPower>('/analytics/site/peak-power', {
    params: {
      siteId,
      dateTime,
    },
    signal,
  })
}

export function getSitePeakPowerQuery(siteId: MaybeRef<string>, dateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SitePeakPower>): UseQueryReturnType<SitePeakPower, Error> {
  return useQuery<SitePeakPower, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_PEAK_POWER, siteId, dateTime],
    queryFn: async ({ signal }) => await getSitePeakPower(unref(siteId), unref(dateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteNercTariffBandCompliance(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<SiteNercTariffBandCompliance> {
  return await getRequest<SiteNercTariffBandCompliance>('/analytics/site/nerc-tariff-compliance', {
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
    signal,
  })
}

export function getSiteNercTariffBandComplianceQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteNercTariffBandCompliance>): UseQueryReturnType<SiteNercTariffBandCompliance, Error> {
  return useQuery<SiteNercTariffBandCompliance, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_NERC_TARIFF_BAND_COMPLIANCE, siteId, startDateTime, endDateTime],
    queryFn: async ({ signal }) => await getSiteNercTariffBandCompliance(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteRuntimesByType(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined): Promise<SiteRunTimesByType[]> {
  return await getRequest<SiteRunTimesByType[]>('/analytics/site/run-times-by-type', {
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
    signal,
  })
}

export function getSiteRuntimesByTypeQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteRunTimesByType[]>): UseQueryReturnType<SiteRunTimesByType[], Error> {
  return useQuery<SiteRunTimesByType[], Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_RUN_TIMES_BY_TYPE, siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteRuntimesByType(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

async function getSiteLongTermForecast(siteId: string, includeHistorical: boolean, signal?: AbortSignal | undefined) {
  return await getRequest<SiteLongTermForecast>('/analytics/site/long-term-forecast', {
    params: {
      siteId,
      includeHistorical,
    },
    signal,
  })
}

export function getSiteLongTermForecastQuery(siteId: MaybeRef<string>, includeHistorical: MaybeRef<boolean>, useQueryOptions?: QueryOptions<SiteLongTermForecast>): UseQueryReturnType<SiteLongTermForecast, Error> {
  return useQuery<SiteLongTermForecast, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_LONG_TERM_FORECAST, siteId, includeHistorical],
    queryFn: async ({ signal }) => await getSiteLongTermForecast(unref(siteId), unref(includeHistorical), signal),
    ...useQueryOptions,
  })
}

async function getSiteShortTermForecast(siteId: string, totalHours: number, signal?: AbortSignal | undefined) {
  return await getRequest<SiteShortTermForecast>('/analytics/site/short-term-forecast', {
    params: {
      siteId,
      totalHours,
    },
    signal,
  })
}

export function getSiteShortTermForecastQuery(siteId: MaybeRef<string>, totalHours: MaybeRef<number>, useQueryOptions?: QueryOptions<SiteShortTermForecast>): UseQueryReturnType<SiteShortTermForecast, Error> {
  return useQuery<SiteShortTermForecast, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_SHORT_TERM_FORECAST, siteId, totalHours],
    queryFn: async ({ signal }) => await getSiteShortTermForecast(unref(siteId), unref(totalHours), signal),
    ...useQueryOptions,
  })
}

async function getSiteEnergyCertificate(siteId: string, startDateTime: string, endDateTime: string, signal?: AbortSignal | undefined) {
  return await getRequest<AssetFamilyEnergyCertificateData>('/analytics/site/irec', {
    params: {
      siteId,
      startDateTime,
      endDateTime,
    },
    signal,
  })
}

export function getSiteEnergyCertificateQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, useQueryOptions?: QueryOptions<AssetFamilyEnergyCertificateData>): UseQueryReturnType<AssetFamilyEnergyCertificateData, Error> {
  return useQuery<AssetFamilyEnergyCertificateData, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, 'site-energy-certificate', siteId, [startDateTime, endDateTime]],
    queryFn: async ({ signal }) => await getSiteEnergyCertificate(unref(siteId), unref(startDateTime), unref(endDateTime), signal),
    ...useQueryOptions,
  })
}

// GET /analytics/site/flow
async function getSiteFlow(siteId: string, signal?: AbortSignal | undefined): Promise<SiteFlow> {
  return await getRequest<SiteFlow>('/analytics/site/flow', {
    signal,
    params: {
      siteId,
    },
  })
}

export function getSiteFlowQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteFlow>): UseQueryReturnType<SiteFlow, Error> {
  return useQuery<SiteFlow, Error>({
    queryKey: [CACHE_KEY_ANALYTICS, CACHE_KEY_SITE_FLOW, siteId],
    queryFn: async ({ signal }) => await getSiteFlow(unref(siteId), signal),
    ...useQueryOptions,
  })
}
